import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { 
  FiSearch, FiX, FiCpu, FiMessageSquare, FiZap, 
  FiArrowRight, FiFileText, FiUser, FiDollarSign,
  FiTrendingUp, FiActivity, FiCommand
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const SUGGESTIONS = [
  { icon: FiTrendingUp, text: "Compare revenue to last quarter", type: "Analysis" },
  { icon: FiFileText, text: "Show overdue bills for Amazon", type: "Query" },
  { icon: FiUser, text: "When is the next payroll run?", type: "Schedule" },
  { icon: FiDollarSign, text: "Run a Profit & Loss for July", type: "Report" }
];

export default function AISearchModal({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery('');
      setResults([]);
      setIsThinking(false);
    }
  }, [isOpen]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setQuery(val);
    
    if (val.length > 3) {
      setIsThinking(true);
      // Simulate AI processing delay
      const timer = setTimeout(() => {
        setIsThinking(false);
        setResults([
          { title: "Revenue Analysis", category: "Reports", path: "/reports/profit-loss", desc: "View detailed breakdown of income for the selected period." },
          { title: "Acme Corp - INV-10042", category: "Invoices", path: "/sales/invoices", desc: "Found 1 overdue invoice matching your query." },
          { title: "Connect New Bank", category: "Actions", path: "/banking/connect", desc: "Link an additional institution to your ledger." }
        ]);
      }, 800);
      return () => clearTimeout(timer);
    } else {
      setResults([]);
      setIsThinking(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4">
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-md" 
      />
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: -20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -20 }}
        className="relative w-full max-w-2xl bg-white rounded-[32px] shadow-2xl shadow-blue-900/20 overflow-hidden border border-white"
      >
        {/* Search Input Area */}
        <div className="relative p-6 bg-slate-50/50 border-b border-slate-100">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${isThinking ? 'bg-blue-600 animate-pulse' : 'bg-white shadow-sm text-slate-400'}`}>
              <SafeIcon icon={isThinking ? FiCpu : FiSearch} className={`text-xl ${isThinking ? 'text-white' : ''}`} />
            </div>
            <input 
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleSearch}
              placeholder="Ask ProfitYo anything... (e.g. 'Show me June revenue')"
              className="flex-1 bg-transparent border-none outline-none text-lg font-bold text-slate-900 placeholder:text-slate-400"
            />
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-slate-200 text-slate-500 rounded-md text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
                <SafeIcon icon={FiCommand} /> K
              </span>
              <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-h-[60vh] overflow-y-auto custom-scrollbar p-6">
          <AnimatePresence mode="wait">
            {query.length === 0 ? (
              <motion.div 
                key="suggestions"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <div>
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 px-2">Suggested Forensic Queries</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {SUGGESTIONS.map((s, i) => (
                      <button 
                        key={i}
                        onClick={() => setQuery(s.text)}
                        className="flex items-center gap-3 p-4 bg-slate-50 hover:bg-blue-50 rounded-2xl text-left transition-all group border border-transparent hover:border-blue-100"
                      >
                        <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 group-hover:text-blue-600 group-hover:border-blue-200 transition-all">
                          <SafeIcon icon={s.icon} className="text-sm" />
                        </div>
                        <div>
                          <p className="text-[13px] font-bold text-slate-700 group-hover:text-slate-900">{s.text}</p>
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{s.type}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            ) : isThinking ? (
              <motion.div 
                key="thinking"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-12 flex flex-col items-center justify-center text-center"
              >
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-xl animate-bounce">
                    <SafeIcon icon={FiCpu} className="text-3xl text-white" />
                  </div>
                  <motion.div 
                    animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-0 bg-blue-400 rounded-2xl -z-10"
                  />
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-1">Analyzing Ledger...</h3>
                <p className="text-sm text-slate-500 font-medium">ProfitYo AI is cross-referencing your transactions.</p>
              </motion.div>
            ) : results.length > 0 ? (
              <motion.div 
                key="results"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-2"
              >
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 px-2">AI Generated Insights</h4>
                {results.map((res, i) => (
                  <button 
                    key={i}
                    onClick={() => { navigate(res.path); onClose(); }}
                    className="w-full flex items-start gap-4 p-4 hover:bg-slate-50 rounded-2xl text-left transition-all group border border-transparent hover:border-slate-100"
                  >
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 group-hover:bg-blue-600 group-hover:text-white transition-all">
                      <SafeIcon icon={FiZap} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className="text-[14px] font-black text-slate-900">{res.title}</span>
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-[9px] font-black uppercase rounded tracking-widest">{res.category}</span>
                      </div>
                      <p className="text-xs text-slate-500 font-medium truncate">{res.desc}</p>
                    </div>
                    <SafeIcon icon={FiArrowRight} className="text-slate-300 mt-2 opacity-0 group-hover:opacity-100 transition-all translate-x-[-10px] group-hover:translate-x-0" />
                  </button>
                ))}
              </motion.div>
            ) : (
              <div className="py-12 text-center">
                <SafeIcon icon={FiMessageSquare} className="text-4xl text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">No direct matches found. Try asking about a specific vendor or date range.</p>
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">
            <span className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div> AI Core v3.0</span>
            <span>•</span>
            <span className="flex items-center gap-1.5"><SafeIcon icon={FiActivity} /> Real-time Sync</span>
          </div>
          <p className="text-[10px] font-bold text-slate-300">ESC to close</p>
        </div>
      </motion.div>
    </div>
  );
}